import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel, InvoiceLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";

export interface GA4ProductListItem {
    event: string;
    add_to_cart_type?: string;
    list_id?: string;
    list_name?: string;
    sign_in?: string;
    authentication_state?: string;
    click_file_extension?: string;
    click_filename?: string;
    quote_id?: string;
    ecommerce: {
        shipping_tier?: string;
        currency?: string;
        value?: number;
        tax?: number;
        shipping?: number;
        affiliation?: string;
        transaction_id?: string;
        coupon?: string;
        items: KeyPair<string, string | number>[];
        payment_type?: string;
    };
}

type KeyPair<K extends string, V> = {
    [key in K]: V;
};

type Item = ProductContextModel | CartLineModel | OrderLineModel;

export const PENDING_SUBMIT_QUOTE_KEY = "pending_submit_quote";
export const getSubmitQuoteFiredKey = (quoteId: string) => `submit_quote_fired_${quoteId}`;

// OOTB from file /modules/client-framework/src/Common/Utilities/tracking.ts
// get global for google tag manager
function getDataLayer() {
    return (window as any).dataLayer;
}

function setData(eventData: any) {
    const dataLayer = getDataLayer();
    if (dataLayer) {
        dataLayer.push(eventData);
    } else {
        (window as any).dataLayer = [eventData];
    }
}

function getItemDetails(item: Item) {
    if ("product" in item) {
        const itemTemp = item as ProductContextModel;
        return {
            product: itemTemp.productInfo != null ? itemTemp.productInfo.productId : itemTemp.product.id,
            quantity: itemTemp.productInfo != null ? itemTemp.productInfo.qtyOrdered : 0,
            price:
                itemTemp.productInfo != null
                    ? itemTemp.productInfo.pricing != null
                        ? itemTemp.productInfo.pricing.actualPrice
                        : itemTemp.product.unitListPrice
                    : (itemTemp.product.unitListPrice ?? 0),
            itemName: `${itemTemp.product.productTitle} - ${itemTemp.product.productNumber}`,
            itemId: itemTemp.productInfo != null ? itemTemp.productInfo.productId : itemTemp.product.id,
            brand: itemTemp.product.brand?.name ?? "",
            variants: itemTemp.product.variantTraits?.map(vt => vt.nameDisplay).join(",") ?? "",
        };
    }

    if ("pricing" in item) {
        const itemTemp = item as CartLineModel;
        return {
            product: itemTemp.productId!,
            quantity: itemTemp.qtyOrdered ?? 0,
            price: itemTemp.pricing?.actualPrice ?? 0,
            itemName: `${itemTemp.shortDescription} - ${itemTemp.erpNumber}`,
            itemId: itemTemp.productId!,
            brand: itemTemp.brand?.name ?? "",
            variants: "",
        };
    }

    if ("invoiceLine" in item) {
        const itemTemp = item.invoiceLine as unknown as InvoiceLineModel;
        return {
            product: itemTemp.id,
            quantity: itemTemp.qtyInvoiced ?? 0,
            price: itemTemp.unitPrice ?? 0,
            itemName: `${itemTemp.productName} - ${itemTemp.productERPNumber}`,
            itemId: itemTemp.id,
            brand: itemTemp.brand?.name ?? "",
            variants: "",
        };
    }

    const itemTemp = item as OrderLineModel;
    return {
        product: itemTemp.productId!,
        quantity: itemTemp.qtyOrdered!.toString(),
        price: itemTemp.unitPrice ?? "",
        itemName: `${itemTemp.shortDescription} - ${itemTemp.productErpNumber}`,
        itemId: itemTemp.productErpNumber,
        brand: itemTemp.brand?.name ?? "",
        variants: "",
    };
}

// items is either a QuoteLineModel[], a CartLineModel[] or a ProductModel[] (or keep adding more types)
export const setGA4Event = (eventName: string, items: any[], additionalInfo?: any) => {
    if (IS_SERVER_SIDE) {
        return;
    }

    let eventData = {
        event: eventName || "",
        ecommerce: {
            items: [],
        },
    } as GA4ProductListItem;

    interface ProductCategory {
        id: string;
        categories: string[];
        brandName: string;
        categoriesIds: string[];
    }

    if (eventName === "select_item") {
        const ga4Items = items;

        ga4Items.forEach((item: ProductContextModel | CartLineModel | OrderLineModel) => {
            const { categories, pageCategoryId } = additionalInfo;

            const itemDetails = getItemDetails(item);

            // pop last value from categories object
            const lastCategoryName = categories[Object.keys(categories)[Object.keys(categories).length - 1]];

            const itemData = {
                item_name: itemDetails.itemName,
                item_id: itemDetails.itemId,
                price: itemDetails.price,
                item_brand: itemDetails.brand,
                ...categories,
                item_variant: itemDetails.variants,
                item_list_name: lastCategoryName ?? "",
                item_list_id: pageCategoryId ?? "",
                index: 1,
                quantity: itemDetails.quantity,
            } as KeyPair<string, string | number>;

            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "view_item") {
        const ga4Items = items;

        ga4Items.forEach(item => {
            const { categories } = additionalInfo;

            const itemDetails = getItemDetails(item);

            const itemData = {
                item_name: itemDetails.itemName,
                item_id: itemDetails.itemId,
                price: itemDetails.price,
                item_brand: itemDetails.brand,
                ...categories,
                item_variant: itemDetails.variants,
                quantity: itemDetails.quantity,
            } as KeyPair<string, string | number>;

            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "add_to_cart") {
        const ga4Items = items;
        let indexCount = 1;
        ga4Items.forEach(item => {
            let { categories, pageCategoryId, brand, qtyOrdered } = additionalInfo;
            const qtysOrdered = additionalInfo?.qtysOrdered as CartLineModel[];
            const productCategories = additionalInfo?.productCategories as ProductCategory[];
            const itemDetails = getItemDetails(item);

            if (productCategories && productCategories.length > 0) {
                const productCategory = productCategories.find(x => x.id === itemDetails.itemId);
                const categoriesToArr = Object.values(productCategory!.categoriesIds);

                categories = productCategory?.categories;
                brand = productCategory?.brandName;
                pageCategoryId = categoriesToArr[categoriesToArr.length - 1] || "";
            }

            if (qtysOrdered) {
                qtyOrdered = qtysOrdered.find(x => x.productId === itemDetails.itemId)?.qtyOrdered;
            }
            // pop last value from categories object
            const lastCategoryName = categories[Object.keys(categories)[Object.keys(categories).length - 1]];

            const itemData = {
                item_name: itemDetails.itemName,
                item_id: itemDetails.itemId,
                price: itemDetails.price,
                item_brand: brand ?? itemDetails.brand,
                ...categories,
                item_variant: itemDetails.variants,
                item_list_name: lastCategoryName ?? "",
                item_list_id: pageCategoryId ?? "",
                index: indexCount,
                quantity: qtyOrdered ?? itemDetails.quantity,
            } as KeyPair<string, string | number>;
            indexCount++;
            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "add_to_wishlist") {
        const ga4Items = items;

        ga4Items.forEach(item => {
            const itemDetails = getItemDetails(item);
            const { categories, pageCategoryId } = additionalInfo;
            // pop last value from categories object
            const lastCategoryName = categories[Object.keys(categories)[Object.keys(categories).length - 1]];

            const itemData = {
                item_name: itemDetails.itemName,
                item_id: itemDetails.itemId,
                price: itemDetails.price,
                item_brand: itemDetails.brand,
                ...categories,
                item_variant: itemDetails.variants,
                item_list_name: lastCategoryName ?? "",
                item_list_id: pageCategoryId ?? "",
                index: 1,
                quantity: itemDetails.quantity,
            } as KeyPair<string, string | number>;

            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "sign_in") {
        const { authenticationState, userId } = additionalInfo;
        eventData = {
            event: eventName || "",
            authentication_state: authenticationState,
            user_id: userId,
            ecommerce: {
                items: [],
            },
        } as GA4ProductListItem;
    }

    if (eventName === "view_item_list") {
        const ga4Items = items;
        ga4Items.forEach(item => {
            const productInfo = createFromProduct(item);
            const { categories, pageCategoryId, indexes, productsPricingPrices } = additionalInfo;
            productInfo.pricing = productsPricingPrices.find((x: { productId: any }) => x.productId === item.id);

            // pop last value from categories object
            const lastCategoryName = categories[Object.keys(categories)[Object.keys(categories).length - 1]];
            const index = indexes.find((x: { id: string }) => x.id === item.id)?.attribute;
            const itemData = {
                item_name: item.productTitle ? `${item.productTitle} - ${item.productNumber}` : "",
                item_id: item.id ?? "",
                price: productInfo.pricing?.actualPrice ?? 0,
                item_brand: item.brand?.name ?? "",
                ...categories,
                item_variant: item.variantTraits?.map((vt: { nameDisplay: any }) => vt.nameDisplay).join(",") ?? "",
                item_list_name: lastCategoryName ?? "",
                item_list_id: pageCategoryId ?? "",
                index: Number(index),
                quantity: productInfo.qtyOrdered,
            } as KeyPair<string, string | number>;

            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "view_cart" || eventName === "begin_checkout" || eventName === "remove_from_cart") {
        if (eventName === "begin_checkout") {
            items = items.filter((item: any) => item.quoteRequired !== true);
        }
        const ga4Items = items;

        ga4Items.forEach(item => {
            const { productProperties } = additionalInfo;

            const categories = productProperties.find(
                (productProperty: { id: string }) => productProperty.id === item.productId,
            )?.categories;

            const categoriesIds = productProperties.find(
                (productProperty: { id: string }) => productProperty.id === item.productId,
            )?.categoriesIds;

            const brand = productProperties.find(
                (productProperty: { id: string }) => productProperty.id === item.productId,
            )?.brand;

            const itemDetails = getItemDetails(item);

            const itemData = {
                item_name: itemDetails.itemName,
                item_id: itemDetails.itemId,
                price: itemDetails.price,
                item_brand: brand ?? itemDetails.brand,
                ...categories,
                item_variant: itemDetails.variants,
                quantity: itemDetails.quantity,
            } as KeyPair<string, string | number>;

            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "add_payment_info") {
        eventData = {
            event: eventName || "",
            ecommerce: {
                payment_type: "",
                items: [],
            },
        } as GA4ProductListItem;
        items = items.filter((item: any) => item.quoteRequired !== true);
        const ga4Items = items;

        ga4Items.forEach(item => {
            const { cart, billTo, shipTo } = additionalInfo;
            let { categories, pageCategoryId, brand } = additionalInfo;
            const productCategories = additionalInfo?.productCategories as ProductCategory[];
            const itemDetails = getItemDetails(item);

            if (productCategories?.length > 0) {
                const productCategory = productCategories.find(x => x.id === itemDetails.itemId);

                categories = productCategory?.categories;
                brand = productCategory?.brandName;
                pageCategoryId = productCategory?.categoriesIds?.[(productCategory?.categoriesIds?.length ?? 0) - 1];
            }

            const itemData = {
                item_name: itemDetails.itemName,
                item_id: itemDetails.itemId,
                price: itemDetails.price,
                item_brand: brand ?? itemDetails.brand,
                ...categories,
                item_variant: itemDetails.variants,
                quantity: itemDetails.quantity,
            } as KeyPair<string, string | number>;
            eventData.ecommerce.payment_type = cart.paymentMethod.description;
            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "add_shipping_info") {
        eventData = {
            event: eventName || "",
            ecommerce: {
                shipping_tier: "",
                items: [],
            },
        } as GA4ProductListItem;
        items = items.filter((item: any) => item.quoteRequired !== true);
        const ga4Items = items;

        ga4Items.forEach(item => {
            const { carrier } = additionalInfo;
            let { categories, pageCategoryId, brand } = additionalInfo;
            const productCategories = additionalInfo?.productCategories as ProductCategory[];
            const itemDetails = getItemDetails(item);

            if (productCategories?.length > 0) {
                const productCategory = productCategories.find(x => x.id === itemDetails.itemId);

                categories = productCategory?.categories;
                brand = productCategory?.brandName;
                pageCategoryId = productCategory?.categoriesIds?.[(productCategory?.categoriesIds?.length ?? 0) - 1];
            }

            const itemData = {
                item_name: itemDetails.itemName,
                item_id: itemDetails.itemId,
                price: itemDetails.price,
                item_brand: brand ?? itemDetails.brand,
                ...categories,
                item_variant: itemDetails.variants,
                quantity: itemDetails.quantity,
            } as KeyPair<string, string | number>;
            eventData.ecommerce.shipping_tier = carrier.description;
            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "purchase") {
        eventData = {
            event: eventName || "",
            ecommerce: {
                currency: "",
                value: 0,
                tax: 0,
                shipping: 0,
                affiliation: "",
                transaction_id: "",
                coupon: "",
                items: [],
            },
        } as GA4ProductListItem;

        items = items.filter((item: any) => item.quoteRequired !== true);
        const ga4Items = items;

        ga4Items.forEach(item => {
            const { cart, billTo, shipTo, orderNumber } = additionalInfo;
            let { categories, pageCategoryId, brand } = additionalInfo;
            const productCategories = additionalInfo?.productCategories as ProductCategory[];
            const itemDetails = getItemDetails(item);

            if (productCategories?.length > 0) {
                const productCategory = productCategories.find(x => x.id === itemDetails.itemId);

                categories = productCategory?.categories;
                brand = productCategory?.brandName;
                pageCategoryId = productCategory?.categoriesIds?.[(productCategory?.categoriesIds?.length ?? 0) - 1];
            }

            const itemData = {
                item_name: itemDetails.itemName,
                item_id: itemDetails.itemId,
                price: itemDetails.price,
                item_brand: brand ?? itemDetails.brand,
                ...categories,
                item_variant: itemDetails.variants,
                quantity: itemDetails.quantity,
            } as KeyPair<string, string | number>;

            eventData.ecommerce.currency = "USD";
            eventData.ecommerce.value = cart.orderGrandTotal;
            eventData.ecommerce.tax = cart.totalTax;
            eventData.ecommerce.shipping = cart.shippingAndHandling;
            eventData.ecommerce.affiliation = billTo.companyName ?? "";
            eventData.ecommerce.transaction_id = orderNumber;
            eventData.ecommerce.coupon = "";
            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "submit_quote") {
        const { quote_id: quoteId } = additionalInfo;
        eventData = {
            event: eventName || "",
            quote_id: quoteId ?? "",
            ecommerce: {
                items: [],
            },
        } as GA4ProductListItem;

        const ga4Items = items;
        ga4Items.forEach(item => {
            const productCategories = additionalInfo?.productCategories as ProductCategory[];
            const itemDetails = getItemDetails(item);
            let { categories, brand } = additionalInfo ?? {};

            if (productCategories?.length > 0) {
                const productCategory = productCategories.find(x => x.id === itemDetails.itemId);
                categories = productCategory?.categories;
                brand = productCategory?.brandName;
            }

            const price =
                item.quoteRequired === true || itemDetails.price === null || itemDetails.price === undefined
                    ? 0
                    : itemDetails.price;

            const itemData = {
                item_name: itemDetails.itemName,
                item_id: itemDetails.itemId,
                price,
                item_brand: brand ?? itemDetails.brand,
                ...categories,
                item_variant: itemDetails.variants || "",
                quantity: itemDetails.quantity,
            } as KeyPair<string, string | number>;

            eventData.ecommerce.items.push(itemData);
        });
    }

    if (eventName === "download_paperwork" || eventName === "file_download") {
        const { fileExtension, fileName } = additionalInfo;
        eventData = {
            event: eventName || "",
            click_file_extension: fileExtension,
            click_filename: fileName,
        } as GA4ProductListItem;
    }

    if (eventName === "create_account" || eventName === "contact_us_form_submission") {
        eventData = {
            event: eventName || "",
        } as GA4ProductListItem;
    }

    // Verndale Customization
    localStorage.removeItem("ga4Array");
    // End Verndale Customization

    setData(eventData);
};
